import { io, type Socket } from "socket.io-client";

import { ConfigService } from "@/shared/services/config.service";
import { TokenStorageService } from "@/shared/services/tokenStorage.service";
import type { UserRole, UserType } from "@/shared/types/user.entity";

import { shouldForwardSocketEvent } from "../config/notification-sources.config";
import {
  NOTIFICATION_SOCKET_EVENTS,
  SOCKET_EVENT_MARK_READ,
  SOCKET_EVENT_MARK_READ_OK,
  SOCKET_EVENT_MARKED_READ_BROADCAST,
} from "../constants/notifications.constants";
import type { NotificationsConnectionStatus } from "../store/notificationsSlice";
import type { NotificationItem } from "../types/notifications-api.types";

const configService = ConfigService.getInstance();

const SOCKET_NAMESPACE = "/operations";

function getSocketOrigin(): string {
  const origin = configService.get("socketOrigin");
  if (origin != null && String(origin).trim() !== "") return String(origin).trim().replace(/\/$/, "");
  const base = configService.getOrThrow("colocaApiUrl");
  return new URL(base).origin;
}

function getSocketUrl(): string {
  return `${getSocketOrigin()}${SOCKET_NAMESPACE}`;
}

function getAuthToken(): string {
  const raw = TokenStorageService.getToken();
  if (!raw) return "";
  return raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`;
}

export type NotificationListener = (notification: NotificationItem, eventName?: string) => void;

/** Payload of the `verification:updated` socket event (KYC/KYB verdict). Broadcast; filter by `identity_id`. */
export interface VerificationSocketPayload {
  identity_id: string;
  organization_id?: string | null;
  status: string;
  level?: string;
  rejection_reason?: string;
}

/** Listener for verification verdicts pushed over the socket. */
export type VerificationUpdateListener = (payload: VerificationSocketPayload) => void;

/**
 * Payload of the `payin:updated` socket event — a payin status flip (settled/confirmed/rejected) pushed the
 * moment the provider webhook lands. Scoped server-side to the owning organization's room; filter by `id`.
 * Advisory only: the REST payin remains the source of truth (consumers keep a polling fallback).
 */
export interface PayinSocketPayload {
  id: string;
  organization_id: string;
  status: string;
  rail: string;
  country: string;
  currency: string;
  gross_amount: string | null;
  fee_total: string | null;
  net_amount: string | null;
  /**
   * Whether the organization is told about this change one by one (coloca-nest `PAYIN_OUTCOME_STATUSES`):
   * false for the pesos of a USDT sale and for every intermediate status. Missing on a backend that predates
   * it.
   */
  announce?: boolean;
}

/** Listener for payin status flips pushed over the socket. */
export type PayinUpdateListener = (payload: PayinSocketPayload) => void;

/**
 * Payload of the `payout:updated` socket event — the mirror of `payin:updated` for dispersions. Scoped
 * server-side to the owning organization's room; filter by `id`.
 */
export interface PayoutSocketPayload {
  id: string;
  organization_id: string;
  status: string;
  rail: string;
  country: string;
  currency: string;
  gross_amount: string | null;
  fee_total: string | null;
  net_amount: string | null;
  failure_reason: string | null;
  /**
   * Whether the organization is told about this change one by one: false for a batch row (the batch tells), a
   * USDT purchase on the desk (a card loaded from pesos included) and every intermediate status. Missing on a
   * backend that predates it.
   */
  announce?: boolean;
}

/** Listener for payout status flips pushed over the socket. */
export type PayoutUpdateListener = (payload: PayoutSocketPayload) => void;

/**
 * Payload of the `wallet:balance_updated` socket event: the wallet as `GET /internal/payments/wallets` serves
 * it, pushed once per operation that moved money. Consumers UPSERT it by `id` (a brand-new currency can
 * arrive) and drop anything whose `updated_at` is older than what they already hold — the emits are
 * serialized server-side but can still reach a client out of order.
 */
export interface WalletBalanceSocketPayload {
  id: string;
  organization_id: string;
  currency: string;
  balance_available: string;
  balance_pending: string;
  balance_blocked: string;
  status: string;
  last_movement_at: string | null;
  updated_at: string;
}

/** Listener for wallet balance pushes. */
export type WalletBalanceUpdateListener = (payload: WalletBalanceSocketPayload) => void;

/**
 * Payload of the `payout_batch:updated` socket event — one push per state change of a payout batch, and one
 * per processed row while it runs (coloca-nest `PayoutBatchUpdatedEventPayload`). Scoped server-side to the
 * owning organization's room plus INTERNAL staff. `sequence` grows with every processed row, so a consumer
 * drops anything older than what it already holds; a full `GET /:id` still wins over any push.
 */
export interface PayoutBatchSocketPayload {
  id: string;
  organization_id: string;
  status: string;
  counters: Record<string, number>;
  progress_pct: number;
  sequence: number;
  last_item: {
    id: string;
    row_number: number;
    status: string;
    failure_code: string | null;
  } | null;
  updated_at: string;
}

/** Listener for payout batch progress pushes. */
export type PayoutBatchUpdateListener = (payload: PayoutBatchSocketPayload) => void;

/**
 * Payload of the `card:activity` socket event — one push per card purchase approved or declined, refund or
 * reversal, at the same moments the cardholder's WhatsApp alerts fire (coloca-nest
 * `CardActivityEventPayload`). Scoped server-side to the owning organization's cards room (sockets whose
 * principal holds `cards:read`) plus INTERNAL staff. `id` is unique per activity; no provider identity
 * travels in it.
 */
export interface CardActivitySocketPayload {
  id: string;
  organization_id: string;
  card_id: string;
  card_last_4: string | null;
  kind: "purchase_approved" | "purchase_declined" | "refund" | "reversal";
  /** Decimal string in `currency`; null when the backend could not read one. */
  amount: string | null;
  currency: string;
  merchant_name: string | null;
  occurred_at: string;
}

/** Listener for card activity pushes. */
export type CardActivityListener = (payload: CardActivitySocketPayload) => void;

/**
 * Payload of the `card:balance_updated` socket event — a card's balance changed outside a purchase: a load or
 * a withdrawal, a move between cards, or a deposit on chain (crypto, or pesos delivered on Polygon).
 * coloca-nest `CardBalanceUpdatedEventPayload`; same rooms as `card:activity`. No amount: the screen reads
 * the card again.
 */
export interface CardBalanceSocketPayload {
  card_id: string;
  organization_id: string | null;
  /** The card movement behind it, when the backend knows it. */
  card_transaction_id: string | null;
  reason: "top_up" | "withdrawal" | "transfer_in" | "transfer_out" | "deposit";
}

/** Listener for card balance pushes. */
export type CardBalanceListener = (payload: CardBalanceSocketPayload) => void;

class NotificationsSocketService {
  private socket: Socket | null = null;
  private listeners: Set<NotificationListener> = new Set();
  private verificationListeners: Set<VerificationUpdateListener> = new Set();
  private payinListeners: Set<PayinUpdateListener> = new Set();
  private payoutListeners: Set<PayoutUpdateListener> = new Set();
  private walletBalanceListeners: Set<WalletBalanceUpdateListener> = new Set();
  private payoutBatchListeners: Set<PayoutBatchUpdateListener> = new Set();
  private cardActivityListeners: Set<CardActivityListener> = new Set();
  private cardBalanceListeners: Set<CardBalanceListener> = new Set();
  private markReadOkListeners: Set<(notificationId: string) => void> = new Set();
  private errorListeners: Set<(message: string) => void> = new Set();
  private reconnectListeners: Set<() => void> = new Set();
  /** Whether this socket connected before: the next `connect` is a reconnection, after a gap with no pushes. */
  private connectedBefore = false;
  private reviveListener: (() => void) | null = null;
  private onStatusChange: ((status: NotificationsConnectionStatus) => void) | null = null;
  private currentUser: { user_type: UserType; role: UserRole } | null = null;

  setOnStatusChange(callback: ((status: NotificationsConnectionStatus) => void) | null): void {
    this.onStatusChange = callback;
  }

  setCurrentUser(user: { user_type: UserType; role: UserRole } | null): void {
    this.currentUser = user;
  }

  private setStatus(status: NotificationsConnectionStatus): void {
    this.onStatusChange?.(status);
  }

  private deliverIfAllowed(eventName: string, payload: NotificationItem): void {
    if (!this.currentUser) return;
    if (!shouldForwardSocketEvent(eventName, this.currentUser)) return;
    this.listeners.forEach((cb) => cb({ ...payload }, eventName));
  }

  connect(): void {
    if (this.socket) return;
    const token = getAuthToken();
    const socketUrl = getSocketUrl();
    this.socket = io(socketUrl, {
      withCredentials: true,
      ...(token ? { auth: { token } } : {}),
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    this.socket.on("connect", () => {
      this.setStatus("connected");
      // Whatever was pushed while the connection was down never arrives: listeners read it again.
      if (this.connectedBefore) this.reconnectListeners.forEach((cb) => cb());
      this.connectedBefore = true;
    });

    // The client stops retrying after `reconnectionAttempts` (a laptop asleep for a few minutes is enough). The
    // next time the browser is back online or the tab is looked at, it tries again; `connect()` is a no-op while
    // it is connected or still retrying on its own.
    const revive = () => {
      if (document.visibilityState === "hidden") return;
      if (this.socket && !this.socket.connected) this.socket.connect();
    };
    this.reviveListener = revive;
    window.addEventListener("online", revive);
    document.addEventListener("visibilitychange", revive);

    NOTIFICATION_SOCKET_EVENTS.forEach((eventName) => {
      this.socket!.on(eventName, (payload: NotificationItem) => {
        this.deliverIfAllowed(eventName, payload);
      });
    });

    this.socket.on("notification:created", (payload: NotificationItem) => {
      this.deliverIfAllowed("notification:created", payload);
    });

    // Verification verdicts (KYC/KYB). Broadcast to all clients; consumers filter by identity_id.
    this.socket.on("verification:updated", (payload: VerificationSocketPayload) => {
      this.verificationListeners.forEach((cb) => cb(payload));
    });

    // Payin status flips (org-room scoped server-side). Consumers filter by payin id.
    this.socket.on("payin:updated", (payload: PayinSocketPayload) => {
      this.payinListeners.forEach((cb) => cb(payload));
    });

    // Payout status flips, same scoping.
    this.socket.on("payout:updated", (payload: PayoutSocketPayload) => {
      this.payoutListeners.forEach((cb) => cb(payload));
    });

    // Wallet balances after an operation committed. Registered as a first-class listener, NOT through
    // `shouldForwardSocketEvent`: that gate is disabled for ORGANIZATION_MEMBER, i.e. exactly the users
    // who own the wallets.
    this.socket.on("wallet:balance_updated", (payload: WalletBalanceSocketPayload) => {
      this.walletBalanceListeners.forEach((cb) => cb(payload));
    });

    // Batch progress, same first-class registration as the wallet balances and for the same reason.
    this.socket.on("payout_batch:updated", (payload: PayoutBatchSocketPayload) => {
      this.payoutBatchListeners.forEach((cb) => cb(payload));
    });

    // Card activity, same first-class registration: members are the ones whose cards it is about.
    this.socket.on("card:activity", (payload: CardActivitySocketPayload) => {
      this.cardActivityListeners.forEach((cb) => cb(payload));
    });

    // Card balance changes outside a purchase, same registration and audience as the card activity.
    this.socket.on("card:balance_updated", (payload: CardBalanceSocketPayload) => {
      this.cardBalanceListeners.forEach((cb) => cb(payload));
    });

    this.socket.on("connect_error", () => {
      this.setStatus("error");
    });

    this.socket.on("disconnect", () => {
      this.setStatus("disconnected");
    });

    const handleMarkedRead = (payload: { notificationId: string }) => {
      this.markReadOkListeners.forEach((cb) => cb(payload.notificationId));
    };
    this.socket.on(SOCKET_EVENT_MARK_READ_OK, handleMarkedRead);
    this.socket.on(SOCKET_EVENT_MARKED_READ_BROADCAST, handleMarkedRead);

    this.socket.on("error", (payload: { message?: string }) => {
      const message = payload?.message ?? "Error";
      this.errorListeners.forEach((cb) => cb(message));
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    if (this.reviveListener) {
      window.removeEventListener("online", this.reviveListener);
      document.removeEventListener("visibilitychange", this.reviveListener);
      this.reviveListener = null;
    }
    this.connectedBefore = false;
    this.reconnectListeners.clear();
    this.listeners.clear();
    this.verificationListeners.clear();
    this.payinListeners.clear();
    this.payoutListeners.clear();
    this.walletBalanceListeners.clear();
    this.payoutBatchListeners.clear();
    this.cardActivityListeners.clear();
    this.markReadOkListeners.clear();
    this.errorListeners.clear();
  }

  /** Sends the read over the socket; false when there is no connection to send it on. */
  markAsRead(notificationId: string): boolean {
    if (!this.socket?.connected) return false;
    this.socket.emit(SOCKET_EVENT_MARK_READ, { notificationId });
    return true;
  }

  /**
   * Called each time the connection comes back after a drop (not on the first connect). Nothing pushed during
   * the gap is replayed, so whatever is shown from pushes should be read again. Returns an unsubscribe
   * function.
   */
  onReconnect(callback: () => void): () => void {
    this.reconnectListeners.add(callback);
    return () => this.reconnectListeners.delete(callback);
  }

  onMarkReadOk(callback: (notificationId: string) => void): () => void {
    this.markReadOkListeners.add(callback);
    return () => this.markReadOkListeners.delete(callback);
  }

  onError(callback: (message: string) => void): () => void {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onNotification(callback: NotificationListener): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /** Subscribe to verification verdicts (`verification:updated`). Returns an unsubscribe function. */
  onVerificationUpdate(callback: VerificationUpdateListener): () => void {
    this.verificationListeners.add(callback);
    return () => this.verificationListeners.delete(callback);
  }

  /** Subscribe to payin status flips (`payin:updated`). Returns an unsubscribe function. */
  onPayinUpdate(callback: PayinUpdateListener): () => void {
    this.payinListeners.add(callback);
    return () => this.payinListeners.delete(callback);
  }

  /** Subscribe to payout status flips (`payout:updated`). Returns an unsubscribe function. */
  onPayoutUpdate(callback: PayoutUpdateListener): () => void {
    this.payoutListeners.add(callback);
    return () => this.payoutListeners.delete(callback);
  }

  /** Subscribe to wallet balance pushes (`wallet:balance_updated`). Returns an unsubscribe function. */
  onWalletBalanceUpdate(callback: WalletBalanceUpdateListener): () => void {
    this.walletBalanceListeners.add(callback);
    return () => this.walletBalanceListeners.delete(callback);
  }

  /** Subscribe to payout batch progress pushes (`payout_batch:updated`). Returns an unsubscribe function. */
  onPayoutBatchUpdate(callback: PayoutBatchUpdateListener): () => void {
    this.payoutBatchListeners.add(callback);
    return () => this.payoutBatchListeners.delete(callback);
  }

  /** Subscribe to card activity pushes (`card:activity`). Returns an unsubscribe function. */
  onCardActivity(callback: CardActivityListener): () => void {
    this.cardActivityListeners.add(callback);
    return () => this.cardActivityListeners.delete(callback);
  }

  /** Subscribe to card balance pushes (`card:balance_updated`). Returns an unsubscribe function. */
  onCardBalanceUpdate(callback: CardBalanceListener): () => void {
    this.cardBalanceListeners.add(callback);
    return () => this.cardBalanceListeners.delete(callback);
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const notificationsSocketService = new NotificationsSocketService();
